import { Component, Input, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { doGetPromise, setParams } from '../../config/core.httpclient-utils';

import * as _ from 'lodash-es';
import { UiService } from '../../services/core.ui.service';
import { StateService } from '@uirouter/angular';
import { getSearchResultParams, getSearchResultState } from '../../pipes/search/search.functions';
import { GfTitleService } from '../../services/core.title.service';

@Component({
    selector: 'gf-search',
    template: require('./search.html')
})
export class SearchComponent implements OnInit {

    @Input() project: any = null;

    loading: any = false;
    results: any = false;
    private searchSubscription: any = null;
    private searchInProgress: boolean = false;
    private lastSearchParams: string = '';

    myProjectsLabel = 'My Projects';
    siteLabel = 'Entire Site';
    selected = [];
    sections: string[] = [];
    searchText = '';

    // Debounce timer for search
    private searchTimer: any = null;

    projectScopeLabel: any = false;
    searchScope = '';
    searchScopeLabel = '';
    searchScopeGlyph = 'map-marker';

    categories = [];

    constructor(
        private uiService: UiService,
        private window: Window,
        private stateService: StateService,
        private httpClient: HttpClient,
        private titleService: GfTitleService
    ) { }

    ngOnInit() {

        this.searchText = _.get(this.stateService.params, 'q', '');

        if (this.project) {
            this.titleService.setTitle('Site Search', this.project.name);
            this.setScope(this.project);
        } else {
            this.titleService.setTitle('Site Search');
            this.setScope(false);
        }

        doGetPromise(this.httpClient, '/api/trove')
            .then(
                (data) => {
                    var items = _.get(data, 'items', []);
                    var cat = _.find(items, (item) => {
                        return item.id == 0;
                    })
                    items = _.filter(items, (item) => { //Remove root
                        return item.id > 0;
                    });

                    const c = (_.get(this.stateService.params, 'c', '') || '').split(',');
                    this.selected = _.filter(items, (item) => { //Remove root
                        return _.find(c, (i) => {
                            return i == item.id;
                        })
                    });

                    const sectionsParam = _.get(this.stateService.params, 's', '') || '';
                    // With raw: true in route config, params are not URL-encoded
                    this.sections = sectionsParam.split(',').filter((s) => s !== '');

                    this.setChildren(cat, items);
                    this.categories = this.flatten(cat, 0);

                    // Do initial search if query text exists
                    if (this.searchText) {
                        this.doSearch();
                    }
                }, (result) => console.log(result)
            );
    };

    protected setScope(p) {
        if (p) {
            this.projectScopeLabel = this.project.name;
            this.searchScope = 'p';
            this.searchScopeLabel = this.projectScopeLabel;
            this.searchScopeGlyph = 'map-marker';
        } else {
            this.projectScopeLabel = false;
            this.searchScope = 's';
            this.searchScopeLabel = this.siteLabel;
            this.searchScopeGlyph = 'globe';
        }
    };

    setPaging(paging) {
        this.stateService
            .go('.', paging)
            .then(() => {
                this.doSearch();
            });
    }

    add = (item) =>  {
        this.selected = this.selected.concat([item]);
    };

    remove = (item) => {
        this.selected = this.selected.filter((item2) => item2 !== item);
    };

    onSectionsChange = (newSections: string[]) => {
        this.sections = [...newSections];
        this.triggerSearch(false);
    };

    onCategoriesChange = () => {
        this.triggerSearch(false);
    };

    /**
     * Triggers a search
     * @param immediate If true, execute immediately; if false, debounce to allow multiple filter clicks
     */
    triggerSearch = (immediate: boolean = false) => {
        // Cancel any pending timer
        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
            this.searchTimer = null;
        }
        
        // Cancel any in-progress search request and reset state
        this.checkCancelRequest();
        this.searchInProgress = false;
        this.lastSearchParams = '';
        
        if (immediate) {
            this.doSearch();
        } else {
            // Wait 1 second for user to finish toggling filters
            this.searchTimer = setTimeout(() => {
                this.searchTimer = null;
                this.doSearch();
            }, 1000);
        }
    };

    /**
     * Updates URL params to reflect current search state
     */
    private updateUrlParams = () => {
        const filteredSections = this.sections.filter((s) => s !== '');
        const filteredCategories = this.selected.map((item) => item.id).filter((id) => id != null);

        var params = {
            q: this.searchText,
            c: filteredCategories.join(',') || null,
            s: _.join(filteredSections, ',') || null,
            p: null,
            page_num: 1
        };

        if (this.searchScope == 'p' && this.project) {
            params.p = this.project.unixName;
        }

        // Update URL without triggering state reload
        this.stateService.go('.', params, { location: true, reload: false });
    };

    searchResultHref = (result) => {
        return this.stateService.href(
            getSearchResultState(result), getSearchResultParams(result)
        );
    };

    goTo = (result) => {
        if (result.section == 'forummessage') {
            doGetPromise(this.httpClient, `/api/forummessage/${result.refId}/location`)
                .then((data: any) => {
                    //Check if the forum belongs to a different project
                    var _params = {
                        unixName: result.project,
                        id: data.forum,
                        thread: data.forumThread,
                        page_num: Math.ceil(data.number / 20)
                    };

                    this.stateService.go('home.project.summary.forum.read', _params, {
                        notify: true,
                        // prevent reload of the current state
                        reload: false,
                        inherit: true
                    });
                },
                    (result) => console.log(result));
        } else if (result.section == 'docman') {
            doGetPromise(this.httpClient, `/api/docman/${result.refId}/file?rel=folders`)
                .then((data) => {
                    var folderId: number = _.get(data, 'rel.folders[0].id', 0),
                        unixName: string = _.get(data, 'rel.folders[0].project.unixName', '');

                    //Check if the forum belongs to a different project
                    var _params = {
                        unixName: unixName,
                        folder: folderId,
                    };

                    this.stateService.go('home.project.summary.docman', _params, {
                        notify: true,
                        // prevent reload of the current state
                        reload: false,
                        inherit: true
                    });
                },
                    (result) => console.log(result));
        }

        else {
            this.stateService.go(
                getSearchResultState(result), getSearchResultParams(result)
            );
        }
    };

    checkCancelRequest() {
        if (this.searchSubscription) {
            this.searchSubscription.unsubscribe();
            this.searchSubscription = null;
        }
    }

    doSearch = () => {
        // Prevent re-entry
        if (this.searchInProgress) {
            return false;
        }
        
        const filteredSections = this.sections.filter((s) => s !== '');
        const filteredCategories = this.selected
            .map((item) => item.id)
            .filter((id) => id != null);

        const params = {
            q: this.searchText,
            c: filteredCategories.length > 0 ? filteredCategories.join(',') : null,
            s: filteredSections.length > 0 ? filteredSections.join(',') : null,
            p: (this.searchScope == 'p' && this.project) ? this.project.unixName : null,
            page_num: _.get(this.stateService.params, 'page_num', 1),
            page_size: _.get(this.stateService.params, 'page_size', 20)
        };

        // Don't search if no query
        if (params.q == '') {
            return false;
        }
        
        // Skip duplicate search (same params as last search)
        const paramsKey = JSON.stringify(params);
        if (paramsKey === this.lastSearchParams) {
            return false;
        }
        this.lastSearchParams = paramsKey;

        // Cancel any pending request before starting new one
        this.checkCancelRequest();
        
        // Set flag to prevent re-entry - keep it true during entire operation
        this.searchInProgress = true;
        
        this.loading = true;
        this.searchSubscription = this.httpClient.get('/api/search/', { params: setParams(params) })
            .subscribe(
                (response) => {
                    this.loading = false;
                    this.searchSubscription = null;
                    this.searchInProgress = false;
                    this.results = _.sortBy(
                        _.get(response, 'items', []),
                        ['matchScore', 'title']
                    ).reverse();
                    
                    // Update URL after search completes
                    this.updateUrlParams();
                },
                () => {
                    this.loading = false;
                    this.searchSubscription = null;
                    this.searchInProgress = false;
                    this.uiService.message.showWarning('Sorry, could not fetch the search results', 'Search Failed');
                    this.results = [];
                }
            );
    };

    setChildren = (item, items) => {
        item.children = [];
        _.each(items, (i) => {
            if (i.parentCategoryId == item.id) {
                this.setChildren(i, items);
                item.children.push(i);
            }
        })
    }

    flatten = (item, depth) => {
        var ret = [];
        _.each(item.children, (i) => {
            i.searchable = item.searchable + ' ' + i.categoryName;
            var itm = _.cloneDeep(i);
            itm.depth = depth;
            itm.label = i.categoryName;
            itm.searchable = item.searchable + ' ' + i.categoryName;
            delete itm.children;
            ret.push(itm);
            ret = ret.concat(this.flatten(i, depth + 1));
        })

        return ret;
    }
}